import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plane, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useTranslation();
  const [nlEmail, setNlEmail] = useState("");
  const [nlLoading, setNlLoading] = useState(false);

  const links = [
    { key: "about", to: "/#how-it-works" },
    { key: "terms", to: "/terms" },
    { key: "privacy", to: "/privacy" },
    { key: "support", to: "mailto:support@onwardflightickets.com" },
  ];

  return (
    <footer id="footer" className="bg-[#1e3a5f] text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-[#f97316] rounded-lg flex items-center justify-center">
                <Plane className="w-5 h-5 text-white -rotate-45" />
              </div>
              <span className="font-['Outfit'] font-bold text-lg">OnwardFlighTickets</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-sm uppercase tracking-wider mb-4 text-white/40">
              Links
            </h4>
            <ul className="space-y-2.5">
              {links.map((link) =>
                link.to.startsWith("mailto:") || link.to.startsWith("/#") ? (
                  <li key={link.key}>
                    <a href={link.to} className="text-sm text-white/70 hover:text-white transition-colors" data-testid={`footer-${link.key}`}>
                      {t(`footer.${link.key}`)}
                    </a>
                  </li>
                ) : (
                  <li key={link.key}>
                    <Link to={link.to} className="text-sm text-white/70 hover:text-white transition-colors" data-testid={`footer-${link.key}`}>
                      {t(`footer.${link.key}`)}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-sm uppercase tracking-wider mb-4 text-white/40">
              Contact
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="mailto:support@onwardflightickets.com"
                  className="text-sm text-white/70 hover:text-white transition-colors flex items-center gap-2"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4" />
                  support@onwardflightickets.com
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/1234567890"
                  className="text-sm text-white/70 hover:text-white transition-colors flex items-center gap-2"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-white/10 pt-8 pb-8">
          <div className="max-w-md mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
              <span className="text-sm font-semibold text-white">Get Travel Deals & Tips</span>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!nlEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nlEmail)) {
                  toast.error("Please enter a valid email");
                  return;
                }
                setNlLoading(true);
                try {
                  const res = await fetch(`${API}/newsletter/subscribe`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: nlEmail, source: "footer" }),
                  });
                  const data = await res.json();
                  if (data.success) {
                    toast.success(data.message);
                    setNlEmail("");
                  } else {
                    toast.error(data.detail || "Failed to subscribe");
                  }
                } catch {
                  toast.error("Failed to subscribe");
                } finally {
                  setNlLoading(false);
                }
              }}
              className="flex gap-2 mt-3"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-[#f97316] outline-none"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={nlLoading}
                className="bg-[#f97316] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-[#ea580c] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="newsletter-subscribe-btn"
              >
                <Send className="w-3.5 h-3.5" /> {nlLoading ? "..." : "Subscribe"}
              </button>
            </form>
            <p className="text-xs text-white/30 mt-2">Join 5,000+ travelers. Unsubscribe anytime.</p>
          </div>
        </div>

        {/* Payment & Copyright */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            &copy; {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-3" data-testid="payment-methods">
            <span className="text-xs text-white/40">{t("footer.payment_methods")}:</span>
            {["Visa", "Mastercard", "PayPal"].map((m) => (
              <span key={m} className="text-xs font-medium text-white/60 bg-white/10 px-2.5 py-1 rounded">
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
